/**
 * WHEN SOMETHING HAPPENED, AS A PERSON READS IT.
 *
 * <p>Gate cards rendered `Answered 01:42 PM` and nothing else, so a decision taken ten days ago was
 * indistinguishable from one taken at lunchtime. The instant is stored in full — the event frame
 * carries `"at":"2026-09-13T09:42:18.630Z"` and the specification's `decisions_recorded[].at`
 * carries `"2026-09-13T09:44:38Z"` — so nothing was missing but the rendering.</p>
 *
 * <p><b>THE COMPARISON IS IN THE VIEWER'S CALENDAR, NOT IN UTC</b>, and that is the whole of the
 * difficulty. A decision taken at 02:00 local on the 14th is `22:00Z` on the <b>13th</b>: comparing
 * UTC dates would call it yesterday while the person looking at it would say today. So the two
 * instants are reduced to local year/month/day and compared there.</p>
 */

/** The date-and-time form, for anything that did not happen today. */
const FULL: Intl.DateTimeFormatOptions = {
  day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
}

/** The time-only form, for today. */
const TIME: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' }

/** Whether two instants fall on the same day in the VIEWER'S zone. */
function sameLocalDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate()
}

/**
 * One instant, rendered for the viewer.
 *
 * <p>Today: the time alone, because the date would be noise on every card of a run someone is
 * watching. Any other day: the date as well, because a bare clock time on a decision from last week
 * is not shorthand, it is wrong.</p>
 *
 * <p><b>The locale supplies the shape</b> — the component order, the separator and whether it is a
 * 12- or 24-hour clock all come from `Intl` rather than from a format string maintained here.
 * `en-GB` gives "13 September 2026 at 13:44", `en-US` "September 13, 2026 at 01:44 PM", `de-DE`
 * "13. September 2026 um 13:44". The connector is the locale's, deliberately.</p>
 *
 * @param iso The instant, as stored. Null, empty or unparseable yields null.
 * @param now What to call today. Defaults to the clock; passed explicitly by tests, which must not
 *   depend on the day they are run.
 * @returns The rendering, or null when there is no instant to render.
 */
export function formatWhen(iso: string | null | undefined, now: Date = new Date()): string | null {
  if (iso === null || iso === undefined || iso.length === 0) return null

  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return null

  return sameLocalDay(at, now)
    ? at.toLocaleTimeString([], TIME)
    : at.toLocaleString([], FULL)
}
